// Libs
import styled from "styled-components";
import React, { useContext, useEffect, useState } from "react";
import { isNullOrEmpty, useObserver, useRouter } from "@shoothill/core";
import { Box, CircularProgress, useMediaQuery, Button, Typography, Select, MenuItem } from "@material-ui/core";
import * as Mui from "@material-ui/core";

// App
import { StoresContext, Stores } from "Globals/Stores";

//SVG's
import { CrossSVG } from "Globals/Views/IconSVGs/CrossSVG";
import { FileSVG } from "Globals/Views/IconSVGs/FileSVG";

// Types
import { UserFile } from "Globals/Models/UserFile";

// View model
import { BasicModel, FileUploaderViewModel } from "Globals/ViewModels/FileUploaderViewModel";

// Styles
import { CustomInput } from "Globals/Styles/AppStyling";
import { Row } from "Globals/Styles/LayoutStyles";

import { ConfirmModal } from "./ConfirmModal";
import SupportingInformationViewModel from "../ViewModels/FormSections/SupportingInformation/SupportingInformationViewModel";
import { getUnixTime } from "date-fns";
import { uniqueId } from "lodash-es";
import { DocumentTypesEnum } from "Globals/Models/DocumentTypesEnum";

export interface IProps {
    text: string;
    loading?: boolean;
    disabled?: boolean;
    files: UserFile[];
    onUpload: (file: UserFile) => any;
    onDelete: (file: UserFile) => any;
    singleFile?: boolean;
    showDocType?: boolean;
    onDocTypeChange?: (index: number, val: string) => void;
}

//Styles
export const Uploader = styled.div`
    background-color: #f0f0f0;
    flex-basis: 100%;
    flex-direction: column;
    padding: 15px;
    border-radius: 5px;

    legend {
        font-size: 12px;
        font-weight: bold;
        color: #191919;
    }

    .upload-drop-area {
        background-color: transparent;
        border-radius: 5px;
        border: 1px #ced4da dashed;
        padding: 45px 0px 45px;
        position: relative;
    }
    img {
        margin: auto;
        height: 112px;

        @media (max-width: 767px) {
            height: 72px;
        }
    }

    input {
        opacity: 0;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
    }

    input,
    input::-webkit-file-upload-button {
        cursor: pointer;
    }

    .upload-placeholder {
        color: #7f7f7f;
        font-size: 12px;
        width: 100%;
        text-align: center;
    }
    .files-uploaded {
        li {
            display: flex;
            align-items: center;
            padding: 15px;
            list-style-type: none;
            border-bottom: 1px #ced4da solid;
            a {
                display: flex;
                align-items: center;
                cursor: pointer;
                margin-right: 30px;
                .file-name {
                    white-space: nowrap;
                    overflow: hidden;
                    width: 250px;
                    text-overflow: ellipsis;
                    font-size: 12px;
                }
                > * {
                    margin-right: 10px;
                }
            }
        }
    }
`;

/**
 * This component can be used to upload files to the blob storage.
 * It accepts a list of files type of (UserFile) and a callback that returns the uploaded (UserFile).
 */
export const FileUploader: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(new FileUploaderViewModel(props.files));

    const [showModal, setShowModal] = useState<boolean>(false);
    const [activeFile, setActiveFile] = useState<UserFile | null>(null);

    //force rerender of component when approved pdf is generated
    const [hasApprovedFormPDF, setHasApprovedFormPDF] = useState<boolean>(false);

    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;
    const showUploadArea = (): boolean => {
        let show = true;
        if (props.singleFile == true && viewModel.filesToDisplay.length > 0) {
            show = false;
        }
        return show;
    };

    useEffect(() => {
        if (storesContext.Domain.DocumentTypeStore.hasApprovedFormPDF) {
            setHasApprovedFormPDF(true);
        }

        /*  if (file.documentTypeId === "") {
                viewModel.setError("documentTypeId", "Document type is required");
            }*/
    });

    const handleShowModal = (file: UserFile) => {
        setActiveFile(file);
        setShowModal(true);
    };

    const confirmDelete = () => {
        viewModel.delete(activeFile as UserFile);
        props.onDelete(activeFile as UserFile);
        setShowModal(false);
        setActiveFile(null);
    };

    //get all document types

    const getApplicantFormPdfFile = () => {
        //get application form pdf type
        const documentType = storesContext.Domain.DocumentTypeStore.getDocumentTypeByType(DocumentTypesEnum.ApprovedApplicantFormPDF);

        return props.files.find((file) => file.documentTypeId == documentType!.id);
    };

    //the approved applicant pdf file is stored in the same table and is not currently editable by the user.

    const getAdminAmendableFiles = () => {
        //get application form pdf type
        const documentType = storesContext.Domain.DocumentTypeStore.getDocumentTypeByType(DocumentTypesEnum.ApprovedApplicantFormPDF);

        return props.files.filter((file) => file.documentTypeId != documentType!.id);
    };

    return useObserver(() => (
        <>
            <ConfirmModal
                active={showModal}
                messageTitle="Warning - Deleting file"
                messageText={`Are you sure you want to delete ${activeFile?.fileName} ?`}
                messageType="error"
                handleOnClose={() => setShowModal(false)}
                handleConfirm={() => confirmDelete()}
            />

            <Uploader>
                <legend>{props.text}</legend>
                {showUploadArea() && props.disabled == false && (
                    <>
                        <div className="upload-drop-area">
                            <>
                                <p className="upload-placeholder">Click this box or drag your file to upload a document</p>
                                <p className="upload-placeholder">( .doc, .pdf, .jpg, .png, .csv )</p>
                            </>
                            <input
                                type="file"
                                accept=".doc,.pdf,.jpg,.png,.csv"
                                disabled={props.disabled || (props.singleFile && viewModel.filesToDisplay.length > 0)}
                                onChange={async (e) => {
                                    let userFile: UserFile | null = await viewModel.upload(e, viewModel.model.documentTypeId);
                                    if (userFile != null) {
                                        userFile.documentTypeId = viewModel.model.documentTypeId;
                                        props.onUpload(userFile);
                                    }
                                }}
                            />
                        </div>
                    </>
                )}
                <ul className="files-uploaded">
                    {getAdminAmendableFiles().map((file, index) => (
                        <li key={uniqueId()}>
                            <a onClick={() => viewModel.download(file.fileUrl, file.fileName)}>
                                <FileSVG className="file-icon" />
                                <span className="file-name">{file.fileName}</span>
                            </a>
                            {props.showDocType && (
                                <>
                                    <Select
                                        label="Document type"
                                        style={isMobile ? { width: "100%" } : { maxWidth: "350px", marginRight: "100px" }}
                                        onChange={async (e: any) => {
                                            if (props.onDocTypeChange) {
                                                props.onDocTypeChange(index, e.target.value);
                                            }
                                        }}
                                        value={file.documentTypeId}
                                    >
                                        {storesContext.Domain.DocumentTypeStore.getDocumentTypeOptions.map((item) => {
                                            return (
                                                <MenuItem key={uniqueId()} value={item.value}>
                                                    {item.key}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    <Box pr={3}>{isNullOrEmpty(file.documentTypeId) && <span style={{ color: "red", fontSize: "12px" }}>Document type is required</span>}</Box>
                                </>
                            )}
                            <a
                                onClick={() => {
                                    handleShowModal(file);
                                }}
                            >
                                <CrossSVG className="file-delete" />
                            </a>
                        </li>
                    ))}
                    {props.files.length == 0 && <li> No files uploaded. </li>}
                </ul>

                {/* generated application form pdf */}

                {getApplicantFormPdfFile() !== undefined && (
                    <>
                        <div>
                            <span style={{ fontSize: 12 }}>Approved application form</span>
                        </div>

                        <ul className="files-uploaded">
                            <li key={uniqueId()}>
                                <a onClick={() => viewModel.download(getApplicantFormPdfFile()!.fileUrl, getApplicantFormPdfFile()!.fileName)}>
                                    <FileSVG className="file-icon" />
                                    <span className="file-name">{getApplicantFormPdfFile()!.fileName}</span>
                                </a>
                            </li>
                        </ul>
                    </>
                )}
            </Uploader>
        </>
    ));
};

FileUploader.defaultProps = {
    showDocType: false,
};
function useeffect(arg0: () => void, arg1: never[]) {
    throw new Error("Function not implemented.");
}
